// FILE: src/components/AchievementBadge.js
//
// AchievementBadge — the single, presentational entry point for rendering an
// achievement badge anywhere in the app. Like CoachAvatar, it knows nothing
// about learners or analytics; callers pass the catalog row's `iconKey` plus
// whether it's been earned:
//
//     <AchievementBadge iconKey="badge-baseline" earned title="Baseline Set" />
//     <AchievementBadge iconKey="badge-certified" earned={false} size={40} />
//
// PHASE E3 (now): iconKey -> file resolution is a flat lookup against assets
// served from /themes/pm-pixel/achievements/. The public API is already
// theme-shaped (mirrors CoachAvatar), so the white-label theme layer can later
// swap the base path / mapping for values from ThemeContext with NO change to
// any call site.
//
// Locked (not-yet-earned) badges render the real art in greyscale at reduced
// opacity — the goal stays visible but clearly not-yet-achieved. Earned badges
// render in full color.
//
// No Redux. Functional component, hooks only.

import React from 'react';
import bcss from './AchievementBadge.module.css';

// ─── Phase-E3 theme stand-in ─────────────────────────────────────────────────
// In the white-label phase this base path comes from the active theme manifest
// (theme.achievements.badgeBasePath). Keeping it as a constant here lets us ship
// the badge art without the theme layer.
const BADGE_BASE_PATH = '/themes/pm-pixel/achievements/';

// Known icon keys — must match the `icon` column in achievement_definitions
// (migration 022). An unknown / null key renders the neutral placeholder dot
// rather than a broken image.
const KNOWN_ICON_KEYS = new Set([
  'badge-baseline',
  'badge-fullsim',
  'badge-threesims',
  'badge-climber',
  'badge-pace',
  'badge-comeback',
  'badge-examready',
  'badge-certified',
  'badge-resilience',
  'badge-founding-tester',
]);

/**
 * @param {string}  iconKey       The catalog row's `icon` value (e.g. 'badge-baseline').
 * @param {boolean} [earned]      Earned -> full color; locked -> greyscale + dimmed.
 * @param {number}  [size]        Displayed square size in px (default 88).
 * @param {string}  [title]       Used for alt text when not decorative.
 * @param {boolean} [decorative]  When true (default), the image is aria-hidden
 *                                with empty alt — the title is already in the DOM
 *                                beside it. Set false to expose alt to AT.
 * @param {string}  [className]   Optional extra class on the wrapper.
 */
export default function AchievementBadge({
  iconKey,
  earned = false,
  size = 88,
  title = '',
  decorative = true,
  className = '',
}) {
  // If the image 404s or fails to decode, fall back to the neutral dot rather
  // than showing a broken-image icon.
  const [failed, setFailed] = React.useState(false);

  const hasArt = KNOWN_ICON_KEYS.has(iconKey) && !failed;

  const wrapperClass = [
    bcss.badge,
    earned ? bcss.earned : bcss.locked,
    className,
  ].filter(Boolean).join(' ');

  // Neutral placeholder when there's no resolvable art (unknown key / load fail).
  if (!hasArt) {
    return (
      <span
        className={`${wrapperClass} ${bcss.placeholder}`}
        style={{ width: size, height: size }}
        data-testid="achievement-badge"
        data-icon={iconKey || 'none'}
        data-earned={earned ? 'true' : 'false'}
        aria-hidden="true"
      />
    );
  }

  return (
    <span
      className={wrapperClass}
      style={{ width: size, height: size }}
      data-testid="achievement-badge"
      data-icon={iconKey}
      data-earned={earned ? 'true' : 'false'}
    >
      <img
        className={bcss.img}
        src={BADGE_BASE_PATH + iconKey + '.png'}
        width={size}
        height={size}
        loading="lazy"
        decoding="async"
        alt={decorative ? '' : (title ? `${title} badge` : 'Achievement badge')}
        aria-hidden={decorative ? 'true' : undefined}
        draggable="false"
        onError={() => setFailed(true)}
      />
    </span>
  );
}
